// eslint-disable-next-line max-classes-per-file
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import translation from 'translation';
import {showNotification} from 'components/common/MNotification/MNotificationManager';
import getRequestHeaders from 'api/getRequestHeaders';
import JSONbig from 'json-bigint';
import handleApiError from 'utils/api/errors/handleApiError';
import ApiUnauthorizedError from 'utils/api/errors/ApiUnauthorizedError';
import {redirectSellerToTR} from 'utils/seller/redirectSellerUtils';
import {isCurrentAppSeller} from 'utils/appTypeState';

function bigIntParser (res) {
  return JSONbig.parse(res);
}

function serializeForSentry (value, maxLen = 10000) {
  try {
    if (value === null) {
      return value;
    }
    const str = typeof value === 'string' ? value : JSON.stringify(value);
    if (str.length > maxLen) {
      return `${str.slice(0, maxLen)}…[truncated]`;
    }
    return str;
  } catch (e) {
    return '[unserializable]';
  }
}

class RequestCanceledError extends Error {
  constructor (message) {
    super(message || '[HttpService] API request canceled');
    this.name = this.constructor.name;
    this.stack = null;
  }
}

// This is the only api service that we should use
class HttpService {
  constructor () {
    this.httpClient = axios.create({});

    this.abortController = new AbortController();

    this.httpClient.interceptors.request.use((config) => {
      try {
        Sentry.addBreadcrumb({
          category: 'http',
          type: 'http',
          data: {
            url: config && config.url,
            method: config && config.method,
            params: config && config.params,
            request_headers: config && config.headers,
            request_body_preview: serializeForSentry(config && config.data)
          },
          level: 'info'
        });
      } catch (e) {
        // no-op
      }
      return config;
    });
    this.httpClient.interceptors.response.use(
      (response) => {
        try {
          Sentry.addBreadcrumb({
            category: 'http',
            type: 'http',
            data: {
              url: response && response.config && response.config.url,
              method: response && response.config && response.config.method,
              status_code: response && response.status,
              status_text: response && response.statusText,
              response_headers: response && response.headers,
              response_body_preview: serializeForSentry(response && response.data)
            },
            level: response && response.status >= 400 ? 'error' : 'info'
          });
        } catch (e) {
          // no-op
        }
        return response;
      },
      (error) => {
        try {
          const response = error && error.response;
          const config = error && error.config || response && response.config || {};
          Sentry.addBreadcrumb({
            category: 'http',
            type: 'http',
            data: {
              url: config && config.url,
              method: config && config.method,
              status_code: response && response.status,
              status_text: response && response.statusText,
              request_headers: config && config.headers,
              params: config && config.params,
              request_body_preview: serializeForSentry(config && config.data),
              response_headers: response && response.headers,
              response_body_preview: serializeForSentry(response && response.data)
            },
            level: 'error'
          });
        } catch (e) {
          // no-op
        }
        return Promise.reject(error);
      }
    );
  }

  cancelAllRequests () {
    this.abortController.abort();

    // create new abort controller, otherwise all new requests will be aborted
    this.abortController = new AbortController();
  }

  /**
   * Get timestamp for request to prevent caching
   * @returns {number}
   */
  getTimestamp () {
    return Math.round(new Date().getTime() / 1000);
  }

  resolveError (err, {hideError} = {}) {
    if (err.message === 'Network Error') {
      showNotification(
        {
          type: 'error',
          header: translation.tr('Error'),
          text: translation.tr('Something terrible happened. Please, refresh the page'),
          closeByButton: true
        });
    }
    if (err.message === 'canceled') {
      throw new RequestCanceledError();
    }

    // show notifications for http errors >= 500
    if (err.response) {
      if (err.response.status >= 500 && !hideError) {
        if (err.response.data.errors && err.response.data.errors.message) {
          showNotification(
            {
              type: 'error',
              header: translation.tr('Error').format(
                err.response.status),
              text: err.response.data.errors.message,
              closeByButton: true
            });
        } else {
          showNotification(
            {
              type: 'error',
              header: translation.tr('Server Error ({0})').format(
                err.response.status),
              text: translation.tr('Please reload page and try again'),
              closeByButton: true
            });
        }
      }
    }

    let throwError = null;

    // throws wrapped error based on error code or error type
    try {
      handleApiError(err);
    } catch (error) {
      if (error instanceof ApiUnauthorizedError && isCurrentAppSeller()) {
        redirectSellerToTR();
      }
      throwError = error;
    }

    throw throwError;
  }

  async post (url, payload, params = {}, axiosParams = {}, {hideError, useBigIntParser = false} = {}) {
    const headers = getRequestHeaders();

    if (params.accountId) {
      headers['X-API-Account'] = params.accountId;
    }

    const customParams = {};
    if (useBigIntParser) {
      customParams.transformResponse = bigIntParser;
    }

    try {
      return await this.httpClient.post(url, payload, {
        signal: this.abortController.signal,
        ...customParams,
        ...axiosParams,
        headers,
        params: params.getParams
      });
    } catch (err) {
      return this.resolveError(err, {hideError});
    }
  }

  async patch (url, payload, params = {}, axiosParams = {}) {
    const headers = getRequestHeaders();

    if (params.accountId) {
      headers['X-API-Account'] = params.accountId;
    }

    try {
      const response = await this.httpClient.patch(url, payload, {
        signal: this.abortController.signal,
        ...axiosParams,
        headers
      });
      return response.data;
    } catch (err) {
      return this.resolveError(err);
    }
  }

  async put (url, payload, params = {}, axiosParams = {}) {
    const headers = getRequestHeaders();

    if (params.accountId) {
      headers['X-API-Account'] = params.accountId;
    }

    try {
      const response = await this.httpClient.put(url, payload, {
        signal: this.abortController.signal,
        ...axiosParams,
        headers
      });
      return response.data;
    } catch (err) {
      return this.resolveError(err);
    }
  }

  async get (url, getParams, params = {}, axiosParams = {}, {hideError, useBigIntParser = false} = {}) {
    getParams = {...getParams, _: this.getTimestamp()};

    const headers = getRequestHeaders();

    if (params.accountId) {
      headers['X-API-Account'] = params.accountId;
    }

    if (params.forceNullAccount) {
      delete headers['X-API-Account'];
    }

    const customParams = {};
    if (useBigIntParser) {
      customParams.transformResponse = bigIntParser;
    }

    try {
      const response = await this.httpClient.get(url, {
        signal: this.abortController.signal,
        ...customParams,
        ...axiosParams,
        headers,
        params: getParams
      });
      return response.data;
    } catch (err) {
      return this.resolveError(err, {hideError});
    }
  }

  async delete (url, queryParams = {}, params = {}, axiosParams = {}) {
    const headers = getRequestHeaders();

    if (params.accountId) {
      headers['X-API-Account'] = params.accountId;
    }

    try {
      const response = await this.httpClient.delete(url, {
        signal: this.abortController.signal,
        ...axiosParams,
        headers,
        params: queryParams
      });
      return response.data;
    } catch (err) {
      return this.resolveError(err);
    }
  }
}

export default new HttpService();

export {HttpService};
